"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import {
  BEACON_ENDPOINT,
  BEACON_PATH_MAX_LENGTH,
  BEACON_REFERRER_MAX_LENGTH,
} from "~/server/analytics/beacon-payload";

/** `/dashboard`, `/sv/dashboard/...` — the admin app never reports itself. */
const DASHBOARD_PATHNAME = /^\/(?:(?:sv|en)\/)?dashboard(?:\/|$)/;

export function isDashboardPathname(pathname: string): boolean {
  return DASHBOARD_PATHNAME.test(pathname);
}

/**
 * Fire and forget. `sendBeacon` survives the page unloading; the `fetch`
 * fallback with `keepalive` is the closest thing where it's missing or the
 * browser refuses to queue (its per-page beacon quota, for instance).
 */
function send(body: string) {
  try {
    if (
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(BEACON_ENDPOINT, body)
    ) {
      return;
    }
  } catch {
    // Fall through to fetch.
  }
  fetch(BEACON_ENDPOINT, { method: "POST", body, keepalive: true }).catch(
    () => {
      // A lost page view is not worth surfacing to the visitor.
    },
  );
}

type Props = {
  locale: AppLocale;
};

/**
 * Reports one page view per route to `/api/beacon`: on first render and on
 * every client-side navigation. Renders nothing and never throws, so the
 * page is never slower or broken because of it.
 */
export function PageViewBeacon({ locale }: Props) {
  const pathname = usePathname();
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || isDashboardPathname(pathname)) return;
    // A re-run for the same route (Strict Mode, a search-param change) is
    // not a new page view.
    if (lastReported.current === pathname) return;

    const isFirstView = lastReported.current === null;
    lastReported.current = pathname;

    try {
      // The query string carries campaign attribution; drop it rather than
      // the whole view when it would push the path past what the endpoint
      // accepts.
      const withQuery = `${pathname}${window.location.search}`;
      const path =
        withQuery.length <= BEACON_PATH_MAX_LENGTH ? withQuery : pathname;
      // Only the first page of a visit knows where the visitor came from;
      // after a client-side navigation `document.referrer` is stale.
      const referrer = isFirstView
        ? document.referrer.slice(0, BEACON_REFERRER_MAX_LENGTH)
        : "";
      send(JSON.stringify({ path, referrer, locale }));
    } catch {
      // Never let analytics break a page.
    }
  }, [pathname, locale]);

  return null;
}
